import { DependencyList, useCallback, useEffect, useState } from "react";

interface ApiState<T> {
  data: T;
  loading: boolean;
  error: boolean;
  reload: () => void;
}

/** Loads data from an API function and tracks loading / error state. */
export default function useApi<T>(
  request: () => Promise<T>,
  initial: T,
  deps: DependencyList = []
): ApiState<T> {
  const [data, setData] = useState<T>(initial);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [version, setVersion] = useState(0);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const run = useCallback(request, deps);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    run()
      .then((result) => {
        if (!cancelled) setData(result ?? initial);
      })
      .catch((err) => {
        console.error(err);
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run, version]);

  const reload = useCallback(() => setVersion((v) => v + 1), []);

  return { data, loading, error, reload };
}
